/**
 * The step registry: what a pipeline step is called and where it sits.
 *
 * This is the only hand-kept table behind the "LLM Config" page and Echo
 * Studio's "Models used" blocks, and it holds NO model names. It knows:
 *
 * - a human label, a pipeline phase and an Echo Studio department for each
 *   ledger step id (`usage_detail.node` / `lane`, else the cost domain: the
 *   same rule as the dashboard API's `step_of`);
 * - which runtime-config assignment (`config_key` + `path`) feeds which
 *   ledger step.
 *
 * Which model runs a step always comes from the live payload (the signed
 * runtime config and the cost ledger). A step or an assignment this file does
 * not know is still shown, with a humanized id, under "Delivery / other".
 */

/**
 * Echo Studio department ids (contracts/echo-studio/echo-studio-catalog.v1.json).
 * Kept as a literal union so this module stays free of the catalog JSON; the
 * Echo Studio selector test checks it against the catalog.
 */
export type StudioDepartment =
  | "lobby"
  | "intake"
  | "story"
  | "writers_room"
  | "production"
  | "sound"
  | "edit_qc"
  | "dispatch"
  | "unclassified";

export type PhaseId =
  | "intake"
  | "interview"
  | "story"
  | "photos"
  | "video"
  | "sound"
  | "edit"
  | "other";

/** Pipeline phases, in the order the page lists them. */
export const PHASES: readonly { id: PhaseId; label: string }[] = [
  { id: "intake", label: "Intake & real-estate scouting" },
  { id: "interview", label: "Interview (Felix)" },
  { id: "story", label: "Story & script" },
  { id: "photos", label: "Photo preparation" },
  { id: "video", label: "Video (clips)" },
  { id: "sound", label: "Sound (voice, music)" },
  { id: "edit", label: "Edit & QC" },
  { id: "other", label: "Delivery / other" },
];

export function phaseLabel(id: PhaseId): string {
  return PHASES.find((phase) => phase.id === id)?.label ?? id;
}

type StepDef = {
  label: string;
  phase: PhaseId;
  department: StudioDepartment;
};

const step = (
  label: string,
  phase: PhaseId,
  department: StudioDepartment,
): StepDef => ({ label, phase, department });

/**
 * Ledger step id → label, phase, department. Order is display order inside a
 * phase. Departments follow the Echo Studio catalog: the real-estate scouting
 * steps sit with `intake.listing` (location scout), photo preparation with
 * `directing.preparation` / `directing.identity` (writers' room), and the
 * composer's own calls on the production floor or in the editing bay.
 */
const STEPS: Record<string, StepDef> = {
  // Intake & real-estate scouting
  real_estate_listing_extraction: step("Listing extraction", "intake", "story"),
  real_estate_brochure_photo_curation: step(
    "Brochure photo curation",
    "intake",
    "story",
  ),
  real_estate_floorplan_read: step("Floor plan reading", "intake", "story"),
  real_estate_site_workflow_planner: step(
    "Agency site workflow planner",
    "intake",
    "story",
  ),
  real_estate_listing_family_ranking: step(
    "Agency site link ranking",
    "intake",
    "story",
  ),
  real_estate_listing_entry_points: step(
    "Agency site entry points",
    "intake",
    "story",
  ),
  real_estate_recipe_synthesis: step(
    "Agency site recipe synthesis",
    "intake",
    "story",
  ),
  room_map: step("Room map", "intake", "story"),
  room_photo_curation: step("Room photo curation", "intake", "story"),
  property_exterior_classification: step(
    "Exterior photo classification",
    "intake",
    "story",
  ),
  signature_detail_extraction: step(
    "Signature details of the property",
    "intake",
    "story",
  ),
  media_analysis: step("Media analysis (every upload)", "intake", "intake"),

  // Interview (Felix)
  orchestrator: step(
    "Felix (interviewer and director graph)",
    "interview",
    "story",
  ),
  voice_intent: step("Narrator voice wishes", "interview", "story"),

  // Story & script
  summary_writer: step("Brief / story summary", "story", "writers_room"),
  style_agent: step("Style selection", "story", "writers_room"),
  narrative_strategy: step("Narrative strategy", "story", "writers_room"),
  script_generator: step("Script and timeline", "story", "writers_room"),
  script_refiner: step("Script refinement", "story", "writers_room"),
  transition_pair_gate: step("Transition pair gate", "story", "writers_room"),
  narration_author: step("Narration author", "story", "writers_room"),
  narration_director: step(
    "Narration director review",
    "story",
    "writers_room",
  ),
  narration_shadow: step("Narration shadow run", "story", "writers_room"),
  narration_repair: step("Narration repair", "story", "writers_room"),
  narration_specificity: step(
    "Narration specificity check",
    "story",
    "writers_room",
  ),

  // Photo preparation
  image_triage_vlm_quality: step(
    "Photo triage: quality check",
    "photos",
    "writers_room",
  ),
  image_triage_similarity_adjudication: step(
    "Photo triage: near-duplicate check",
    "photos",
    "writers_room",
  ),
  dog_census: step("Dog census", "photos", "writers_room"),
  cast_appearance: step("Cast appearance", "photos", "writers_room"),
  character_sheet: step("Character sheets", "photos", "writers_room"),
  image_hd_upscaling: step("Photo HD upscaling", "photos", "writers_room"),
  portrait_person_retake: step(
    "Portrait reframing (person retake)",
    "photos",
    "writers_room",
  ),
  listing_vertical_extension: step(
    "Listing photo vertical extension",
    "photos",
    "writers_room",
  ),
  listing_watermark_cleanup: step(
    "Listing photo watermark cleanup",
    "photos",
    "writers_room",
  ),
  storyboard_parse: step("Storyboard reading", "photos", "writers_room"),
  storyboard_first: step("Storyboard panels", "photos", "writers_room"),
  front_page_editor: step("Front page: editor", "photos", "writers_room"),
  front_page_plate: step("Front page: plate image", "photos", "writers_room"),
  front_page_reader: step(
    "Front page: plate readers",
    "photos",
    "writers_room",
  ),
  front_page_judge: step("Front page: plate judges", "photos", "writers_room"),
  subject_grounding: step(
    "Smart crop: subject grounding",
    "photos",
    "production",
  ),
  bbox: step("Smart crop: subject boxes", "photos", "production"),

  // Video (clips)
  i2v_motion_planner: step("Motion planner", "video", "writers_room"),
  i2v_prompt_director: step("Clip prompt director", "video", "writers_room"),
  h3_shot_module_director: step("H3 shot scouts", "video", "writers_room"),
  continuity_scout: step("Continuity scout", "video", "writers_room"),
  i2v: step("Video clips (image to video)", "video", "production"),
  i2v_qc: step("Clip QC (semantic check)", "video", "production"),

  // Sound (voice, music)
  voice_agent: step("Voice casting", "sound", "writers_room"),
  narrator_cast_classifier: step(
    "Narrator cast classifier",
    "sound",
    "writers_room",
  ),
  music_agent: step("Music planning", "sound", "writers_room"),
  music_palette_classifier: step(
    "Music palette classifier",
    "sound",
    "writers_room",
  ),
  composition_plan: step("Music composition plan", "sound", "sound"),
  music: step("Music generation", "sound", "sound"),
  voice: step("Narration voice (text to speech)", "sound", "sound"),

  // Edit & QC
  title_render: step("Title cards", "edit", "edit_qc"),
  title_bleed_assess: step("Title fit check", "edit", "edit_qc"),
  title_bleed_shorten: step("Title shortening", "edit", "edit_qc"),

  // Delivery / other
  poster_source_selection: step("Poster: photo selection", "other", "edit_qc"),
  poster_prompt_composition: step("Poster: prompt", "other", "edit_qc"),
  poster_generation: step("Poster image", "other", "edit_qc"),
  studio_recut_chat: step("Studio re-cut chat", "other", "dispatch"),
};

const STEP_ORDER = new Map(Object.keys(STEPS).map((id, index) => [id, index]));

/** Every step id this registry names (for tests and audits). */
export const registeredStepIds = (): string[] => Object.keys(STEPS);

/** Cost domains that hold many steps: the step is the node, never the domain. */
const MULTI_STEP_DOMAINS = new Set(["llm", "media_qc"]);

const H3_SCOUT = /^h3_(.+)_scout$/;

export function humanizeId(value: string): string {
  const text = value.replace(/[._-]+/g, " ").trim();
  return text ? text[0].toUpperCase() + text.slice(1) : value;
}

export type StepInfo = {
  id: string;
  label: string;
  phase: PhaseId;
  department: StudioDepartment;
  /** False for a step this registry does not know. */
  known: boolean;
  /** Display order: registry order first, unknown steps after. */
  order: number;
};

/** Label, phase and department of a step id, known or not. */
export function stepInfo(id: string): StepInfo {
  const known = STEPS[id];
  if (known) return { id, ...known, known: true, order: STEP_ORDER.get(id)! };
  const scout = H3_SCOUT.exec(id);
  if (scout) {
    return {
      id,
      label: `H3 shot scout: ${scout[1].replaceAll("_", " ")}`,
      phase: "video",
      department: "writers_room",
      known: true,
      order: STEP_ORDER.get("h3_shot_module_director")!,
    };
  }
  return {
    id,
    label: humanizeId(id),
    phase: "other",
    department: "unclassified",
    known: false,
    order: Number.MAX_SAFE_INTEGER,
  };
}

/**
 * The registry id of a ledger row's step. `step` is what the dashboard API
 * reports (`usage_detail.node` or `lane`, else the domain). A single-purpose
 * domain (`voice`, `i2v`, `title_render`…) is one step whatever its node says.
 */
export function ledgerStepId(domain: string, stepName: string): string {
  if (STEPS[stepName] || H3_SCOUT.test(stepName)) return stepName;
  if (!MULTI_STEP_DOMAINS.has(domain) && STEPS[domain]) return domain;
  return stepName || domain;
}

/**
 * The step of one cost-ledger row, mirroring the dashboard API's `step_of`:
 * the domain defaults to `i2v` (legacy rows), the step is the node, else the
 * lane, else the domain.
 */
export function stepOfCostRow(row: {
  cost_domain?: string | null;
  usage_detail?: Record<string, string> | null;
}): { domain: string; stepId: string } {
  const domain = (row.cost_domain ?? "").trim().toLowerCase() || "i2v";
  const raw =
    row.usage_detail?.node?.trim() || row.usage_detail?.lane?.trim() || domain;
  return { domain, stepId: ledgerStepId(domain, raw) };
}

/** Real-estate gateway lanes whose ledger node is not `real_estate_<lane>`. */
const REAL_ESTATE_LANES: Record<string, readonly string[]> = {
  brochure_curation: ["real_estate_brochure_photo_curation"],
  floorplan_read_multipage: ["real_estate_floorplan_read"],
  site_link_ranking: [
    "real_estate_listing_family_ranking",
    "real_estate_listing_entry_points",
  ],
  studio_recut_chat: ["studio_recut_chat"],
};

const IMAGE_TRIAGE_ROLES: Record<string, string> = {
  dog_census: "dog_census",
  cast_appearance: "cast_appearance",
  vlm_quality: "image_triage_vlm_quality",
  similarity_adjudication: "image_triage_similarity_adjudication",
  character_sheets: "character_sheet",
};

const under = (path: string, prefix: string) =>
  path === prefix || path.startsWith(`${prefix}.`);

export type AssignmentTarget = {
  /** Ledger steps this assignment feeds. */
  steps: string[];
  /**
   * A model named by the config that is not a step's route (a prompt target,
   * for example): listed under "Every model reference", not in the main table.
   */
  referenceOnly: boolean;
};

/**
 * Which ledger step(s) a runtime-config assignment feeds, or null when this
 * registry does not know. `observedSteps` lets one route fan out to the steps
 * actually seen in the ledger (the H3 shot scouts).
 */
export function assignmentTarget(
  configKey: string,
  path: string,
  observedSteps: readonly string[] = [],
): AssignmentTarget | null {
  const to = (...steps: string[]): AssignmentTarget => ({
    steps,
    referenceOnly: false,
  });
  switch (configKey) {
    case "orchestrator.llm_routes.v1": {
      if (!path) return null;
      const role = path.split(".")[0];
      if (role === "h3_shot_module_director") {
        // One route authors every H3 shot module; each scout is its own node.
        const scouts = [...new Set(observedSteps)].filter((id) =>
          H3_SCOUT.test(id),
        );
        return to(...(scouts.length ? scouts.sort() : [role]));
      }
      // The front-page editor is built on the script generator's route.
      if (role === "script_generator")
        return to("script_generator", "front_page_editor");
      // A role is its own ledger node (script_generator, i2v_prompt_director…).
      return to(role);
    }
    case "orchestrator.narration_plan.policy.v1":
      if (path === "") return to("narration_author");
      if (under(path, "director_review")) return to("narration_director");
      return null;
    case "bot.real_estate.llm_routes.v1": {
      if (!under(path, "lanes") || path === "lanes") return null;
      const lane = path.split(".")[1];
      return to(...(REAL_ESTATE_LANES[lane] ?? [`real_estate_${lane}`]));
    }
    case "orchestrator.image_triage.policy.v1": {
      const role = IMAGE_TRIAGE_ROLES[path.split(".")[0]];
      return role ? to(role) : null;
    }
    case "orchestrator.image_hd_upscaling.policy.v1":
      return to("image_hd_upscaling");
    case "orchestrator.portrait_person_retake.policy.v2":
      return to("portrait_person_retake");
    case "orchestrator.listing_vertical_extension.policy.v1":
      return to("listing_vertical_extension");
    case "orchestrator.listing_watermark_cleanup.policy.v1":
      return to("listing_watermark_cleanup");
    case "composer.i2v.routing.contract":
      if (under(path, "routes")) return to("i2v");
      // The model a prompt is authored for: a reference, not a route.
      if (under(path, "required_prompt_targets"))
        return { steps: ["i2v"], referenceOnly: true };
      return null;
    case "composer.i2v.qc.semantic_model_id":
      return to("i2v_qc");
    case "composer.framing.subject_grounding.policy":
      return to("subject_grounding");
    case "audio.voice_render_profiles.v1":
      return to("voice");
    case "orchestrator.b2c.front_page.policy.v1":
      if (path === "plate") return to("front_page_plate");
      // Both plate readers answer as reader and as judge.
      if (
        under(path, "plate_qc.reader_route") ||
        under(path, "plate_qc.second_reader_route")
      )
        return to("front_page_reader", "front_page_judge");
      return null;
    default:
      return null;
  }
}
